import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { value: '30,000+', label: '影视资源' },
    { value: '500万+', label: '日活跃用户' },
    { value: '99.9%', label: '服务可用率' },
    { value: '8年', label: '稳定运营' },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: '免费观影',
      description: '被窝网电影始终坚持免费原则，所有影视资源无需注册、无需付费即可在线观看，让每个人都能享受到优质影视内容。',
    },
    {
      icon: 'ri-speed-up-line',
      title: '极速流畅',
      description: '采用先进的视频压缩与CDN加速技术，多线路智能切换，确保高清画质下依然流畅不缓冲，给你最佳的观影体验。',
    },
    {
      icon: 'ri-refresh-line',
      title: '每日更新',
      description: '专业内容团队每日同步更新最新影视资源，院线大片快速上线，热播剧集同步更新，精彩内容永不断档。',
    },
    {
      icon: 'ri-device-line',
      title: '多端适配',
      description: '完美适配电脑、平板、手机等各类设备，无论你身处何地，都能随时随地畅享影视盛宴。',
    },
  ];

  const contactMethods = [
    {
      icon: 'ri-mail-line',
      title: '电子邮箱',
      value: 'contact@beiwomovie.com',
      description: '商务合作、版权问题、意见建议',
    },
    {
      icon: 'ri-customer-service-line',
      title: '在线客服',
      value: '7×24小时在线',
      description: '随时为您解答观影问题',
    },
    {
      icon: 'ri-wechat-line',
      title: '微信公众号',
      value: '被窝网电影',
      description: '关注获取最新影视资讯',
    },
    {
      icon: 'ri-weibo-line',
      title: '官方微博',
      value: '@被窝网电影',
      description: '实时影视动态分享',
    },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '被窝网电影',
    url: 'https://www.beiwomovie.com',
    description: '被窝网电影是领先的免费高清影视资源聚合平台，提供海量电影、电视剧、动漫、综艺等优质内容在线观看。',
    foundingDate: '2017',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@beiwomovie.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.beiwomovie.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="w-full relative h-[320px] md:h-[400px] overflow-hidden bg-foreground-950">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/40 via-foreground-950 to-foreground-950"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              关于被窝网电影
            </h1>
            <p className="text-white/70 text-sm md:text-base max-w-2xl">
              始于2017年，致力于为用户提供最优质的免费高清影视观看体验
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full bg-background-50 -mt-12 relative z-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 text-center"
                  >
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-foreground-500 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div className="w-16 h-1 bg-primary-500 mx-auto rounded-full"></div>
              </div>

              <div className="space-y-6 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  <strong className="text-foreground-900">被窝网电影</strong>创立于2017年，是中国领先的免费高清影视资源聚合平台。我们的使命是让每个人都能轻松享受到优质的影视内容，无需付费、无需复杂操作，打开网站即可畅享电影和电视剧带来的乐趣。
                </p>
                <p>
                  经过多年的发展，被窝网电影已经成长为中国最具影响力的免费影视平台之一。我们收录了超过<strong className="text-foreground-900">30,000部</strong>影视资源，涵盖电影、电视剧、动漫、综艺、纪录片等多种类型，满足不同用户的观影需求。
                </p>
                <p>
                  被窝网电影始终坚持<strong className="text-foreground-900">「免费、高清、流畅」</strong>三大核心理念。我们采用先进的视频压缩技术，在保证高清画质的同时，大幅降低了带宽消耗，让用户即使在移动网络环境下也能流畅观看。多线路智能切换技术确保了服务的稳定性，无论何时何地，都能享受到不缓冲的观影体验。
                </p>
                <p>
                  我们的内容团队每日更新最新影视资源，确保用户能第一时间观看到院线热门大片和最新电视剧。同时，我们还提供智能推荐功能，根据用户的观影偏好精准推荐相关内容，让发现好片变得更加简单。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  核心优势
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  我们用心做好每一个细节，只为给你最好的观影体验
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {values.map((value, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 text-center group"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-5 rounded-2xl bg-primary-100 group-hover:bg-primary-200 transition-colors">
                      <i className={`${value.icon} text-2xl text-primary-600`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  有任何问题或建议，欢迎随时与我们取得联系
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactMethods.map((method, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-5 bg-background-100 rounded-2xl"
                  >
                    <div className="w-12 h-12 flex items-center justify-center shrink-0 rounded-xl bg-primary-100">
                      <i className={`${method.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                        {method.title}
                      </h3>
                      <p className="text-primary-600 text-sm font-medium mb-1">{method.value}</p>
                      <p className="text-foreground-400 text-xs">{method.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Copyright Notice */}
              <div className="mt-10 p-5 bg-background-100 rounded-2xl">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">
                  版权声明
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  被窝网电影所有影视资源均来自互联网公开资源，本站不存储、不制作任何影视内容，仅提供资源聚合与导航服务。如涉及版权问题，请通过上述联系方式与我们沟通，我们将第一时间处理。本站坚决支持正版，倡导用户通过正规渠道观看影视作品。
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}